// §19.14 / item H — Test-script download component.
//
// Rendered by AgentBuilderChat when the Super Agent emits a fenced
// ```test_script_offer block in its reply. The block carries
// {agent_id} — this component renders a small card with a format
// dropdown + Download button. On click, fetches POST /api/agents/<id>/test-script
// (the existing _generate_test_script tool exposed via HTTP), decodes
// the base64 file content, and triggers a browser download.
//
// We don't go through the LLM for the actual script generation — the
// HTTP route hits the same backend implementation directly. Faster,
// fewer tokens, no risk of the LLM stripping b64 from its summary.

import React, { useState } from "react";
import { COLORS } from "../../theme/colors";
import { agentApi } from "../../api/client";


const FORMATS = [
  { value: "pytest",             label: "pytest (Python CI)" },
  { value: "conversation_jsonl", label: "JSONL (replayable)" },
  { value: "curl_sh",            label: "curl bash (CLI)" },
  { value: "postman",            label: "Postman collection" },
];


function _b64ToBlob(b64, mime) {
  const bin = atob(b64);
  const len = bin.length;
  const bytes = new Uint8Array(len);
  for (let i = 0; i < len; i++) bytes[i] = bin.charCodeAt(i);
  return new Blob([bytes], { type: mime });
}


function _mimeFor(fmt) {
  if (fmt === "postman") return "application/json";
  if (fmt === "curl_sh") return "text/x-shellscript";
  if (fmt === "pytest")  return "text/x-python";
  return "application/x-ndjson";
}


export default function TestScriptOffer({ agentId, agentName }) {
  const [format, setFormat] = useState("pytest");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);
  const [downloadedFilename, setDownloadedFilename] = useState(null);

  const handleDownload = async () => {
    if (!agentId) return;
    setBusy(true);
    setErr(null);
    try {
      const res = await agentApi.generateTestScript(agentId, format);
      if (!res.success || !res.data?.content_b64) {
        throw new Error(res.data?.error || "no content returned");
      }
      const blob = _b64ToBlob(res.data.content_b64, _mimeFor(format));
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = res.data.filename || `agent_test.${format === "pytest" ? "py" : format === "postman" ? "json" : format === "curl_sh" ? "sh" : "jsonl"}`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      setDownloadedFilename(res.data.filename);
    } catch (e) {
      setErr(e?.message || "download failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div style={{
      marginTop: 8, marginBottom: 4,
      border: `1px solid ${COLORS.border}`,
      borderRadius: 12,
      background: COLORS.bg,
      padding: 12,
    }}>
      <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 8 }}>
        <span style={{ fontSize: 16 }}>🧪</span>
        <div style={{ flex: 1 }}>
          <div style={{ fontSize: 13, fontWeight: 600, color: COLORS.text }}>
            Starter test script
          </div>
          <div style={{ fontSize: 11, color: COLORS.textDim, marginTop: 2 }}>
            {agentName ? `For ${agentName} — ` : ""}runnable scaffolding to validate the agent end-to-end. Edit + extend before relying on it for prod.
          </div>
        </div>
      </div>

      <div style={{
        display: "flex", gap: 8, alignItems: "center",
        paddingTop: 8, borderTop: `1px solid ${COLORS.border}`,
      }}>
        <select
          value={format}
          onChange={(e) => setFormat(e.target.value)}
          disabled={busy}
          style={{
            flex: 1,
            padding: "6px 10px",
            borderRadius: 6,
            border: `1px solid ${COLORS.border}`,
            background: COLORS.surface,
            color: COLORS.text,
            fontSize: 12,
            cursor: busy ? "not-allowed" : "pointer",
          }}
        >
          {FORMATS.map((opt) => (
            <option key={opt.value} value={opt.value}>{opt.label}</option>
          ))}
        </select>
        <button
          type="button"
          onClick={handleDownload}
          disabled={busy || !agentId}
          style={{
            padding: "6px 14px",
            borderRadius: 6,
            border: "none",
            background: busy ? COLORS.surfaceHover : COLORS.accent,
            color: busy ? COLORS.textDim : "#fff",
            fontSize: 12, fontWeight: 600,
            cursor: busy ? "not-allowed" : "pointer",
          }}
        >
          {busy ? "Generating…" : "Download"}
        </button>
      </div>

      {err && (
        <div style={{
          marginTop: 8, padding: "6px 10px",
          borderRadius: 6,
          background: `${COLORS.pink}15`,
          color: COLORS.pink,
          fontSize: 11,
        }}>{err}</div>
      )}
      {downloadedFilename && !err && (
        <div style={{
          marginTop: 8, padding: "6px 10px",
          borderRadius: 6,
          background: `${COLORS.green}15`,
          color: COLORS.green,
          fontSize: 11,
        }}>✓ Downloaded {downloadedFilename}</div>
      )}
    </div>
  );
}


// Helper for AgentBuilderChat — extracts the test_script_offer block.
// Same pattern as extractTreeProposal.
export function extractTestScriptOffer(content) {
  if (typeof content !== "string") return { offer: null, contentWithoutBlock: content };
  const fenceRx = /```test_script_offer\s*\n([\s\S]*?)\n```/m;
  const match = fenceRx.exec(content);
  if (!match) return { offer: null, contentWithoutBlock: content };
  let offer = null;
  try { offer = JSON.parse(match[1].trim()); } catch { return { offer: null, contentWithoutBlock: content }; }
  const before = content.slice(0, match.index).trimEnd();
  const after = content.slice(match.index + match[0].length).trimStart();
  return { offer, contentWithoutBlock: [before, after].filter(Boolean).join("\n\n") };
}
